<template>
  <div class="transcripts-container flex flex-col gap-4">
    <div class="flex flex-col gap-3">
      <BotEditorSubHeader
        :title="$t('botEditor.transcripts.title')"
        :subtitle="$t('botEditor.transcripts.subtitleAccordion')"
      />

      <div class="flex flex-wrap items-center gap-2">
        <Select
          v-model="channelFilter"
          :options="channelOptions"
          optionLabel="label"
          optionValue="value"
          :placeholder="$t('botEditor.transcripts.channel.filterLabel')"
          class="w-44"
          size="small"
        />
        <TagMultiSelect
          v-model="tagFilter"
          :workspace-id="workspaceId"
          :bot-id="currentConversationsBotId"
          :placeholder="$t('botEditor.transcripts.tags.filterLabel')"
          :creatable="false"
          class="w-56"
        />
        <InputText id="input-transcript-search" v-model="searchQuery" @input="onSearchInput" :placeholder="$t('botEditor.transcripts.searchPlaceholder')"
                   size="small" class="w-64" />
        <Button
          data-telemetry-id="btn-download-filtered-xlsx"
          severity="secondary"
          size="small"
          :label="$t('botEditor.transcripts.export.bulk')"
          :loading="downloadingBulkXlsx"
          @click="downloadFilteredConversationsXlsx"
        >
          <template #icon>
            <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">download</span>
          </template>
        </Button>
      </div>
    </div>

    <!-- Transcript Source Toggle (sempre visibile per debug) -->
    <div class="mb-4 flex items-center gap-4 bg-slate-800/50 p-4 rounded-lg border border-slate-700">
      <label class="text-sm font-medium text-gray-300">
        {{ $t('botEditor.transcripts.source.label') }}
      </label>
      
      <SelectButton
        v-model="transcriptSource"
        :options="transcriptSourceOptions"
        optionLabel="label"
        optionValue="value"
        :allowEmpty="false"
      />
    </div>

    <!-- Date Range Selector -->
    <div class="mb-2">
      <AnalyticsDateRange
        v-model:range="selectedRange"
        v-model:customFrom="customFrom"
        v-model:customTo="customTo"
        @update="fetchConversations(1)"
      />
    </div>

    <div v-if="loadingConversations" class="text-center text-gray-400 py-8">
      <ProgressSpinner
        style="width: 2rem; height: 2rem; margin-bottom: 0.75rem"
        stroke-width="3"
        :aria-label="$t('common.loading')"
      />
      <p class="text-sm">{{ $t('botEditor.transcripts.loadingText') }}</p>
    </div>

    <div v-else-if="transcriptSource === 'production' && !productionBotId" class="text-center text-gray-500 py-8 text-sm bg-gray-800 border border-gray-700 rounded-md">
      {{ $t('botEditor.transcripts.noProductionBot') }}
    </div>

    <div v-else-if="!conversations.length" class="text-center text-gray-500 py-8 text-sm">
      {{ $t('botEditor.transcripts.noData') }}
    </div>

    <div v-else id="conversations-list" class="flex flex-col gap-2">
      <!-- Conversations: one single-panel Accordion per row, so each row is its own rounded
           card (border, radius, header colours and chevron from the preset `accordion`)
           with the list's gap between cards. Open rows live in expandedConvIds (any number
           at once). `lazy` mounts a body only while its row is open; the messages load the
           first time a row opens (onConversationToggle). -->
      <Accordion
        v-for="conv in conversations"
        :key="conv._id"
        lazy
        :value="expandedConvIds.includes(conv._id) ? conv._id : null"
        @update:value="(v) => onConversationToggle(conv._id, v)"
      >
        <AccordionPanel :value="conv._id">
          <!-- pt padding: keep the old px-4 py-3 row density (the preset header is 0.5rem 0.75rem) -->
          <AccordionHeader
            data-telemetry-id="btn-conversation-accordion"
            :pt="{ root: { style: { padding: '0.75rem 1rem' } } }"
          >
            <!-- font-normal: the preset's header weight (500) is meant for one-line titles;
                 only the first line below is medium, as before -->
            <div class="flex-1 min-w-0 mr-3 text-left font-normal">
              <!-- First or Last Message -->
              <div class="font-medium text-sm text-gray-200 truncate">"{{ conv.firstUserMessage || conv.lastMessage || '—' }}"</div>
              <div class="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                <!-- Channel Badge -->
                <Tag
                  v-if="conv.channel"
                  :value="$t('botEditor.transcripts.channel.' + conv.channel)"
                  :severity="channelSeverity(conv.channel)"
                  rounded
                />
                <!-- WhatsApp phone number -->
                <span v-if="conv.phoneNumber" class="flex items-center gap-1">
                  <span class="material-icons select-none" style="font-size: 13px" aria-hidden="true">phone</span>
                  {{ conv.phoneNumber }}
                </span>
                <!-- Message Count-->
                <span class="flex items-center gap-1">
                  <span class="material-icons select-none" style="font-size: 13px" aria-hidden="true">chat_bubble_outline</span>
                  {{ conv.messageCount }} {{ conv.messageCount === 1 ? $t('botEditor.transcripts.message') : $t('botEditor.transcripts.messages') }}
                </span>
                <!-- Last Message Date -->
                <span class="flex items-center gap-1">
                  <span class="material-icons select-none" style="font-size: 13px" aria-hidden="true">schedule</span>
                  {{ formatDate(conv.firstUserMessageDate || conv.lastMessageDate) }}
                </span>
              </div>
              <!-- Conversation ID — click to copy; .stop keeps the click from reaching the
                   header button, so the row does not toggle -->
              <div
                v-tooltip.bottom="!copiedIds[conv._id] ? $t('botEditor.transcripts.copyIdTooltip') : undefined"
                data-telemetry-id="btn-copy-conversation-id"
                class="flex items-center gap-2 mt-1 w-fit cursor-pointer group"
                @click.stop="handleCopyId(conv._id)"
              >
                <span
                  class="font-mono text-xs transition-colors truncate"
                  :class="copiedIds[conv._id] ? 'text-cyan-400' : 'text-gray-600 group-hover:text-gray-300'"
                >{{ copiedIds[conv._id] ? $t('botEditor.transcripts.copiedId') : 'ID: ' + conv._id }}</span>
                <span
                  class="material-icons select-none transition-colors shrink-0"
                  style="font-size: 13px"
                  aria-hidden="true"
                  :class="copiedIds[conv._id] ? 'text-cyan-400' : 'text-gray-600 group-hover:text-gray-300'"
                >{{ copiedIds[conv._id] ? 'check' : 'content_copy' }}</span>
              </div>
            </div>
          </AccordionHeader>

          <!-- Messages inside accordion (body look: CONVERSATION_CONTENT_PT) -->
          <AccordionContent :pt="CONVERSATION_CONTENT_PT">
            <div v-if="loadingMessages[conv._id]" class="px-4 py-6 text-center text-gray-400">
              <ProgressSpinner
                style="width: 1.5rem; height: 1.5rem"
                stroke-width="4"
                :aria-label="$t('common.loading')"
              />
            </div>

            <div v-else>
              <!-- TODO: A second fetch to the last page would be needed to also show the last 2 messages.
                   Even when conv.messageCount < 25 (page size) the last messages would already be available
                   from the page-1 fetch, but showing last 2 was decided against for consistency. -->

              <!-- Preview rows: first 3 messages -->
              <div
                v-for="msg in (conversationMessages[conv._id]?.items || []).slice(0, MESSAGES_TO_DISPLAY_IN_ACCORDION)"
                :key="msg._id"
                class="px-4 py-2 text-sm text-gray-300 border-b border-gray-700"
                :class="msg._id === highlightedMessageId ? 'border-l-2 border-l-red-400 bg-red-500/10' : ''"
                :data-highlighted="msg._id === highlightedMessageId ? 'true' : undefined"
              >
                <span class="font-medium text-gray-400 mr-2">{{ mapAuthor(msg.role) }}:</span>
                <span class="truncate">{{ msg.text || '-' }}</span>
                <span v-if="msg._id === highlightedMessageId" class="ml-2 text-xs font-medium text-red-300">{{ $t('botEditor.transcripts.errorMessageHighlight') }}</span>
              </div>

              <!-- Deep link: the message tied to the error, when it falls outside the preview -->
              <div
                v-if="outOfPreviewHighlight(conv._id)"
                class="px-4 py-2 text-sm text-gray-300 border-b border-gray-700 border-l-2 border-l-red-400 bg-red-500/10"
                data-highlighted="true"
              >
                <span class="font-medium text-gray-400 mr-2">{{ mapAuthor(outOfPreviewHighlight(conv._id).role) }}:</span>
                <span class="truncate">{{ outOfPreviewHighlight(conv._id).text || '-' }}</span>
                <span class="ml-2 text-xs font-medium text-red-300">{{ $t('botEditor.transcripts.errorMessageHighlight') }}</span>
              </div>

              <!-- "…and N more" line -->
              <div v-if="conv.messageCount > MESSAGES_TO_DISPLAY_IN_ACCORDION" class="px-4 py-2 text-xs text-gray-500 italic">
                {{ $t('botEditor.transcripts.previewMoreMessages', { count: conv.messageCount - MESSAGES_TO_DISPLAY_IN_ACCORDION }) }}
              </div>

              <!-- Load Conversation button + Context toggle -->
              <div class="px-4 py-3 flex items-center gap-2">
                <Button
                  data-telemetry-id="btn-load-transcript"
                  severity="primary"
                  size="small"
                  @click="handleLoadTranscript(conv._id, conversationMessages[conv._id]?.items || [])"
                >
                  <span class="material-icons">open_in_new</span>
                  {{ $t('botEditor.transcripts.loadConversation') }}
                </Button>
                <Button
                  data-telemetry-id="btn-toggle-context"
                  severity="secondary"
                  size="small"
                  @click="toggleContext(conv._id)"
                >
                  <span class="material-icons">data_object</span>
                  {{ $t('botEditor.transcripts.context.toggleButton') }}
                </Button>
                <Button
                  data-telemetry-id="btn-download-transcript-xlsx"
                  severity="secondary"
                  size="small"
                  :label="$t('botEditor.transcripts.export.single')"
                  :loading="downloadingXlsx[conv._id]"
                  @click="downloadConversationXlsx(conv._id)"
                >
                  <template #icon>
                    <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">download</span>
                  </template>
                </Button>
              </div>

              <div v-if="contextExpanded[conv._id]" class="px-4 pb-3 border-t border-gray-800 bg-gray-900/50">
                <div v-if="loadingContext[conv._id]" class="py-3 text-center text-gray-400 text-xs">
                  <ProgressSpinner
                    style="width: 1rem; height: 1rem"
                    stroke-width="6"
                    :aria-label="$t('common.loading')"
                  />
                </div>
                <div v-else-if="!contextEntries(conv._id).length" class="py-3 text-xs text-gray-500 italic">
                  {{ $t('botEditor.transcripts.context.empty') }}
                </div>
                <dl v-else class="py-2 divide-y divide-gray-800">
                  <div v-for="[key, value] in contextEntries(conv._id)" :key="key" class="py-2">
                    <dt class="text-xs font-mono text-gray-500">{{ key }}</dt>
                    <dd class="text-sm text-gray-200 font-mono whitespace-pre-wrap break-words">{{ formatContextValue(value) }}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>

      <!-- Conversations Pagination -->
      <div v-if="conversations.length" id="conversations-pagination" class="mt-6 flex items-center justify-center gap-2 px-4 py-3 border border-gray-700 rounded-md bg-gray-800">
        <span class="text-xs text-gray-400">
          {{ $t('botEditor.transcripts.page') }} {{ currentPage }} / {{ totalPages }}
          ({{ total }} {{ $t('botEditor.transcripts.conversations') }})
        </span>
        <div class="flex gap-2">
          <Button
            v-if="currentPage > 1"
            id="btn-prev-page"
            severity="secondary"
            size="small"
            :label="$t('botEditor.transcripts.previous')"
            @click="loadPage(currentPage - 1)"
          />
          <Button
            v-if="currentPage < totalPages"
            id="btn-next-page"
            severity="primary"
            size="small"
            :label="$t('botEditor.transcripts.next')"
            @click="loadPage(currentPage + 1)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import api from '../../services/api';
import BotEditorSubHeader from '../../components/ui/BotEditorSubHeader.vue';
import AnalyticsDateRange from '../../components/analytics/AnalyticsDateRange.vue';
import TagMultiSelect from '../../components/flow/TagMultiSelect.vue';
import { useTranscriptViewer } from '../../composables/useTranscriptViewer';
import { copyToClipboard } from '../../utils/clipboard';

const MESSAGES_TO_DISPLAY_IN_ACCORDION = 3;

const selectedRange = ref('30d');
const customFrom = ref(null);
const customTo = ref(null);

// Deep link: la conversazione puo' essere fuori dal periodo di default, quindi per quella
// fetch il filtro data si salta.
const skipDateRange = ref(false);

const getDateRange = () => {
  if (skipDateRange.value) return {};
  if (selectedRange.value === 'custom') {
    return { from: customFrom.value || undefined, to: customTo.value || undefined };
  }
  const now = new Date();
  const y = now.getFullYear(), mo = now.getMonth(), d = now.getDate();
  let from, to;
  switch (selectedRange.value) {
    case '1d':
      from = new Date(y, mo, d, 0, 0, 0, 0);
      to   = new Date(y, mo, d, 23, 59, 59, 999);
      break;
    case '7d': {
      const day = now.getDay();
      const mondayOffset = day === 0 ? -6 : 1 - day;
      from = new Date(y, mo, d + mondayOffset, 0, 0, 0, 0);
      to   = new Date(y, mo, d + mondayOffset + 6, 23, 59, 59, 999);
      break;
    }
    case '30d':
      from = new Date(y, mo, 1, 0, 0, 0, 0);
      to   = new Date(y, mo + 1, 0, 23, 59, 59, 999);
      break;
    case '90d': {
      const q = Math.floor(mo / 3);
      from = new Date(y, q * 3, 1, 0, 0, 0, 0);
      to   = new Date(y, (q + 1) * 3, 0, 23, 59, 59, 999);
      break;
    }
    default:
      return {};
  }
  return { from: from.toISOString(), to: to.toISOString() };
};

const route = useRoute();
const { t } = useI18n();
const { loadTranscript } = useTranscriptViewer();

const copiedIds = ref({});

// Messaggio da evidenziare (deep link dalla tabella "Errori da sistemare").
const highlightedMessageId = ref(null);
const outOfPreviewHighlight = (convId) => {
  if (!highlightedMessageId.value) return null;
  const items = conversationMessages.value[convId]?.items || [];
  const idx = items.findIndex((m) => m._id === highlightedMessageId.value);
  return idx >= MESSAGES_TO_DISPLAY_IN_ACCORDION ? items[idx] : null;
};

const handleCopyId = async (convId) => {
  await copyToClipboard(convId);
  copiedIds.value = { ...copiedIds.value, [convId]: true };
  setTimeout(() => {
    const next = { ...copiedIds.value };
    delete next[convId];
    copiedIds.value = next;
  }, 1500);
};

const handleLoadTranscript = (convId, items) => {
  const mapped = items.map(msg => ({
    text: msg.text || '',
    type: msg.type || null,
    role: msg.role === 'assistant' ? 'ai' : 'user',
    createdAt: msg.createdAt || null,
    source: msg.role === 'assistant' ? (msg.source || null) : null,
    attachments: msg.attachments || []
  }));
  loadTranscript(convId, mapped);
};

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const downloadConversationXlsx = async (convId) => {
  if (downloadingXlsx.value[convId]) return;
  downloadingXlsx.value = { ...downloadingXlsx.value, [convId]: true };
  try {
    const url = `/workspaces/${workspaceId.value}/bot-conversations/${currentConversationsBotId.value}/conversations/${convId}/export`;
    const res = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: XLSX_MIME_TYPE });
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `transcript-${convId}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  } catch (e) {
    Console.error('Failed to download conversation XLSX', e);
  } finally {
    downloadingXlsx.value = { ...downloadingXlsx.value, [convId]: false };
  }
};

const botId = ref(route.params.id);
const workspaceId = computed(() => route.params.workspaceId);

const conversations = ref([]);
const conversationMessages = ref({});
const loadingConversations = ref(false);
const loadingMessages = ref({});
// Ids of the open conversation rows (each row's Accordion reads its own entry)
const expandedConvIds = ref([]);
const conversationContext = ref({}); // convId -> context object | null
const contextLoaded = ref({}); // convId -> true once a load has been attempted (success or failure)
const loadingContext = ref({});
const contextExpanded = ref({});
const downloadingXlsx = ref({});
const searchQuery = ref('');
const currentPage = ref(1);
const totalPages = ref(1);
const total = ref(0);
const pageSize = 20;

// Channel filter
const channelFilter = ref('');

// Tag filter
const tagFilter = ref([]);
const channelOptions = computed(() => [
  { label: t('botEditor.transcripts.channel.all'), value: '' },
  { label: t('botEditor.transcripts.channel.web'), value: 'web' },
  { label: t('botEditor.transcripts.channel.whatsapp'), value: 'whatsapp' },
  { label: t('botEditor.transcripts.channel.telegram'), value: 'telegram' },
  { label: t('botEditor.transcripts.channel.msteams'), value: 'msteams' },
  { label: t('botEditor.transcripts.channel.slack'), value: 'slack' },
  { label: t('botEditor.transcripts.channel.messenger'), value: 'messenger' },
  { label: t('botEditor.transcripts.channel.sms'), value: 'sms' },
  { label: t('botEditor.transcripts.channel.voice'), value: 'voice' },
]);

// Colore del badge per canale: WhatsApp verde (brand), web neutro, gli altri info.
const channelSeverity = (channel) => ({
  whatsapp: 'success',
  web: 'secondary',
  telegram: 'info',
  messenger: 'info',
  msteams: 'info',
  slack: 'contrast',
  sms: 'warn',
}[channel] || 'info');

// State per il toggle transcripts con persistenza localStorage
const transcriptSource = ref('test');
const testBotId = ref(null);
const productionBotId = ref(null);
const currentConversationsBotId = ref(null); // Track which botId was used to load conversations

let searchTimer = null;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Carica la sorgente di transcripts da localStorage
 * Fallback a 'test' se non trovato o invalido
 */
const loadTranscriptSourceFromStorage = () => {
  if (!botId.value) return 'test';
  const storageKey = `transcript-source-${botId.value}`;
  const stored = localStorage.getItem(storageKey);
  return (stored && ['test', 'production'].includes(stored)) ? stored : 'test';
};

/**
 * Salva la sorgente di transcripts in localStorage
 */
const saveTranscriptSourceToStorage = (source) => {
  if (!botId.value) return;
  const storageKey = `transcript-source-${botId.value}`;
  localStorage.setItem(storageKey, source);
};

/**
 * Carica i dati del bot per ottenere testBotId e productionBotId
 */
const loadBotData = async () => {
  if (!botId.value) {
    Console.warn('[TRANSCRIPTS] botId is null, skipping loadBotData');
    return;
  }

  Console.group('[TRANSCRIPTS] loadBotData');
  try {
    Console.log('botId:', botId.value);
    const resp = await api.get(`/workspaces/${workspaceId.value}/bots/${botId.value}`);
    const bot = resp.data.data || resp.data;

    // Il bot corrente è sempre il test bot
    testBotId.value = botId.value;
    // productionBotId può essere null se il test bot non ha un bot di produzione
    productionBotId.value = bot.productionBotId || null;

    Console.log('Bot data loaded:', {
      testBotId: testBotId.value,
      productionBotId: productionBotId.value
    });
  } catch (e) {
    Console.error('Failed to load bot data:', e.message);
  } finally {
    Console.groupEnd();
  }
};

const transcriptSourceOptions = computed(() => [
  { label: t('botEditor.transcripts.source.test'), value: 'test' },
  { label: t('botEditor.transcripts.source.production'), value: 'production' },
]);

const fetchConversations = async (page = 1) => {
  loadingConversations.value = true;
  try {
    // Il bot corrente è sempre il test bot
    let currentBotId = testBotId.value;
    
    if (transcriptSource.value === 'production') {
      // Se non c'è productionBotId, non fare la fetch
      if (!productionBotId.value) {
        Console.log('[TRANSCRIPTS] Production bot not available');
        conversations.value = [];
        currentPage.value = 1;
        totalPages.value = 0;
        total.value = 0;
        loadingConversations.value = false;
        return;
      }
      currentBotId = productionBotId.value;
    }
    
    Console.group('[TRANSCRIPTS] fetchConversations');
    Console.log('Request:', {
      testBotId: testBotId.value,
      productionBotId: productionBotId.value,
      currentBotId,
      transcriptSource: transcriptSource.value,
      page,
      pageSize,
      url: `/bot-conversations/${currentBotId}/conversations`
    });

    const resp = await api.get(`/workspaces/${workspaceId.value}/bot-conversations/${currentBotId}/conversations`, {
      params: { page, limit: pageSize, ...getDateRange(), ...(searchQuery.value ? { search: searchQuery.value } : {}), ...(channelFilter.value ? { channel: channelFilter.value } : {}), ...(tagFilter.value.length ? { tags: tagFilter.value.join(',') } : {}) }
    });

    Console.log('Response:', {
      currentBotId,
      transcriptSource: transcriptSource.value,
      itemsCount: resp.data.items?.length || 0,
      total: resp.data.total,
      page: resp.data.page,
      pages: resp.data.pages,
      conversationIds: resp.data.items?.map(c => ({ id: c._id, messageCount: c.messageCount, creatorUserId: c.creatorUserId }))
    });
    Console.groupEnd();
    
    conversations.value = resp.data.items || [];
    currentPage.value = resp.data.page;
    totalPages.value = resp.data.pages;
    total.value = resp.data.total;
    currentConversationsBotId.value = currentBotId;
    
    // Reset expanded conversations when page changes
    expandedConvIds.value = [];
  } catch (e) {
    Console.error('Failed to load conversations', e);
  } finally {
    loadingConversations.value = false;
  }
};

const downloadingBulkXlsx = ref(false);

const downloadFilteredConversationsXlsx = async () => {
  if (downloadingBulkXlsx.value) return;
  downloadingBulkXlsx.value = true;
  try {
    const url = `/workspaces/${workspaceId.value}/bot-conversations/${currentConversationsBotId.value}/conversations/export`;
    const res = await api.get(url, {
      params: { ...getDateRange(), ...(searchQuery.value ? { search: searchQuery.value } : {}), ...(channelFilter.value ? { channel: channelFilter.value } : {}), ...(tagFilter.value.length ? { tags: tagFilter.value.join(',') } : {}) },
      responseType: 'blob',
    });
    const blob = new Blob([res.data], { type: XLSX_MIME_TYPE });
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = 'conversations-export.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  } catch (e) {
    Console.error('Failed to download filtered conversations XLSX', e);
  } finally {
    downloadingBulkXlsx.value = false;
  }
};

// A row's Accordion emits its panel value on open and null on close. Opening adds the id
// to expandedConvIds and loads the messages the first time; rows whose messages are
// loaded or still loading are not fetched again, so closing and reopening a row reuses
// what it already has.
const onConversationToggle = (convId, value) => {
  if (value !== convId) {
    expandedConvIds.value = expandedConvIds.value.filter((id) => id !== convId);
    return;
  }
  if (!expandedConvIds.value.includes(convId)) {
    expandedConvIds.value = [...expandedConvIds.value, convId];
  }
  if (!conversationMessages.value[convId] && !loadingMessages.value[convId]) {
    loadConversationMessages(convId, 1);
  }
};

// Conversation row body, as the rows looked before: no padding (the preview rows bring
// their own px-4 and rules), the gray-900 floor tone, and a rule under the header in the
// content's own border colour. The preset's Accordion content (gray-800, padded) suits
// the disclosure sections, and Tailwind classes lose to PrimeVue's unlayered CSS here,
// so the style goes through `pt`.
const CONVERSATION_CONTENT_PT = {
  content: { style: { padding: '0', backgroundColor: 'var(--p-surface-900)', borderTopWidth: '1px' } }
};

const loadConversationMessages = async (convId, page = 1) => {
  loadingMessages.value[convId] = true;
  Console.group('[TRANSCRIPTS] loadConversationMessages');
  try {
    const url = `/workspaces/${workspaceId.value}/bot-conversations/${currentConversationsBotId.value}/conversations/${convId}/messages`;
    const params = { page, limit: 25 };
    Console.log('Request:', {
      botId: botId.value,
      currentConversationsBotId: currentConversationsBotId.value,
      convId,
      page,
      limit: 25,
      url,
      params
    });
    const resp = await api.get(url, { params });
    Console.log('Response:', {
      itemsCount: resp.data.items?.length || 0,
      total: resp.data.total,
      page: resp.data.page,
      pages: resp.data.pages,
      items: resp.data.items
    });
    conversationMessages.value[convId] = {
      items: resp.data.items || [],
      page: resp.data.page,
      pages: resp.data.pages,
      total: resp.data.total
    };
  } catch (e) {
    Console.error('Failed to load conversation messages', {
      error: e.message,
      response: e.response?.data,
      status: e.response?.status,
      convId,
      botId: botId.value,
      currentConversationsBotId: currentConversationsBotId.value
    });
  } finally {
    Console.groupEnd();
    loadingMessages.value[convId] = false;
  }
};

const loadConversationContext = async (convId) => {
  loadingContext.value[convId] = true;
  try {
    const url = `/workspaces/${workspaceId.value}/bot-conversations/${currentConversationsBotId.value}/conversations/${convId}/context`;
    const resp = await api.get(url);
    conversationContext.value = { ...conversationContext.value, [convId]: resp.data.context };
  } catch (e) {
    Console.error('Failed to load conversation context', e);
    conversationContext.value = { ...conversationContext.value, [convId]: null };
  } finally {
    loadingContext.value[convId] = false;
    contextLoaded.value = { ...contextLoaded.value, [convId]: true };
  }
};

const toggleContext = async (convId) => {
  contextExpanded.value = { ...contextExpanded.value, [convId]: !contextExpanded.value[convId] };
  if (contextExpanded.value[convId] && !contextLoaded.value[convId]) {
    await loadConversationContext(convId);
  }
};

const formatContextValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const contextEntries = (convId) => {
  const context = conversationContext.value[convId];
  if (!context) return [];
  return Object.entries(context).filter(([key]) => !key.startsWith('__'));
};

const loadPage = (page) => {
  fetchConversations(page);
};

const onSearchInput = () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    if (searchQuery.value.length >= 2 || searchQuery.value.length === 0) {
      fetchConversations(1);
    }
  }, SEARCH_DEBOUNCE_MS);
};

const mapAuthor = (role) => {
  if (role === 'assistant') return t('botEditor.transcripts.author.bot');
  if (role === 'user') return t('botEditor.transcripts.author.user');
  return t('botEditor.transcripts.author.system');
};

const formatDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleString();
};

// Watcher per il cambio della sorgente di transcripts
// Durante il deep link la sorgente cambia ma non va ne' salvata ne' rifetchata dal watcher.
const applyingDeepLink = ref(false);

watch(transcriptSource, () => {
  if (applyingDeepLink.value) return;
  saveTranscriptSourceToStorage(transcriptSource.value);
  currentPage.value = 1;
  fetchConversations(1);
});

// Watcher per il filtro canale
watch(channelFilter, () => {
  if (applyingDeepLink.value) return;
  fetchConversations(1);
});

// Watcher per il filtro tag
watch(tagFilter, () => {
  if (applyingDeepLink.value) return;
  fetchConversations(1);
});

// Watcher per il cambio di botId (quando la rotta cambia)
watch(() => route.params.id, async (newBotId) => {
  botId.value = newBotId;
  if (newBotId) {
    await loadBotData();
    fetchConversations(1);
  }
});

// Deep link ?conversation=&message=&source=: cerca la conversazione, la apre e
// segna il messaggio dell'errore.
const applyDeepLink = async () => {
  const convId = route.query.conversation;
  if (!convId) return false;
  const id = String(convId);
  applyingDeepLink.value = true;
  skipDateRange.value = true;
  try {
    if (route.query.source === 'production' || route.query.source === 'test') {
      transcriptSource.value = route.query.source;
    }
    channelFilter.value = '';
    tagFilter.value = [];
    searchQuery.value = id;
    // i watcher (sorgente, canale, tag) girano al prossimo flush: li si lascia passare con il flag acceso
    await nextTick();
    applyingDeepLink.value = false;
    await fetchConversations(1);
    if (conversations.value.some((c) => c._id === id)) {
      highlightedMessageId.value = route.query.message ? String(route.query.message) : null;
      onConversationToggle(id, id);
    }
  } finally {
    applyingDeepLink.value = false;
    skipDateRange.value = false;
  }
  return true;
};

onMounted(async () => {
  // Carica i dati del bot (testBotId e productionBotId)
  await loadBotData();
  
  // Carica la sorgente di transcripts da localStorage
  const savedSource = loadTranscriptSourceFromStorage();
  transcriptSource.value = savedSource;
  
  // Fetch iniziale se botId è disponibile
  if (botId.value) {
    if (!(await applyDeepLink())) fetchConversations(1);
  }
});
</script>

<style scoped>
@reference "../../style.css";

.transcripts-container {
  @apply flex flex-col gap-4;
}

.table-header { @apply text-left py-2 px-3 text-xs font-semibold text-gray-400 border-b border-gray-600; }
.table-row { @apply hover:bg-gray-800/50 transition; }
.table-cell { @apply py-2 px-3 border-b border-gray-700 text-sm; }
</style>
